package com.shadowings.gero.presentation.component.quiz

import androidx.compose.animation.core.tween
import androidx.compose.material3.MaterialTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.pager.PagerState
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.navigation.NavController
import androidx.navigation.compose.rememberNavController
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.component.GeroButton
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.util.DevicePreviews
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.avanti_excl_btn
import gero.composeapp.generated.resources.combo_coins_bonus
import gero.composeapp.generated.resources.combo_feedback
import gero.composeapp.generated.resources.combo_subtitle_5_correct
import gero.composeapp.generated.resources.combo_title
import io.github.vinceglb.confettikit.compose.ConfettiKit
import io.github.vinceglb.confettikit.core.Party
import io.github.vinceglb.confettikit.core.Position
import io.github.vinceglb.confettikit.core.emitter.Emitter
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import com.shadowings.gero.util.SoundPlayer
import kotlin.time.DurationUnit
import kotlin.time.toDuration

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun QuizComboFiveComposablePreview() {
    AppTheme {
        Surface {
            QuizComboFiveComposable(
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData()
            )
        }
    }
}

@Composable
fun QuizComboFiveComposable(
    page: Int,
    pagerState: PagerState,
    navController: NavController,
    quizList: MutableState<List<IQuizContent>>,
    userData: UserData
) {
    val scope = rememberCoroutineScope()
    val isVisible = pagerState.currentPage == page
    val showConfetti = remember { mutableStateOf(false) }
    LaunchedEffect(isVisible) {
        if (isVisible) {
            showConfetti.value = true
            SoundPlayer.playComboFive()
        }
    }

    androidx.compose.foundation.layout.BoxWithConstraints(
        Modifier.fillMaxSize().semantics { testTag = "quiz_combo_five" }) {
        val isLandscape = maxWidth > maxHeight

        if (isLandscape) {
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(horizontal = 24.dp, vertical = 16.dp),
                horizontalArrangement = Arrangement.spacedBy(24.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Column 1: Achievement Titles
                Column(
                    modifier = Modifier.weight(1f),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    Text(
                        text = stringResource(Res.string.combo_title),
                        style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth()
                            .semantics { testTag = "combo_five_title" }
                    )
                    Text(
                        text = stringResource(Res.string.combo_subtitle_5_correct),
                        style = MaterialTheme.typography.titleMedium,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth()
                            .semantics { testTag = "combo_five_subtitle" }
                    )
                    Text(
                        text = stringResource(Res.string.combo_coins_bonus),
                        style = MaterialTheme.typography.bodyMedium,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth()
                            .semantics { testTag = "combo_five_subtitle" }
                    )
                }

                // Column 2: Feedback & Button
                Column(
                    modifier = Modifier.weight(1f),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    AlynFeedbackBox(
                        face = Faces.Happy,
                        text = stringResource(Res.string.combo_feedback),
                        userData = userData,
                        isLandscape = true
                    )

                    GeroButton(
                        title = stringResource(Res.string.avanti_excl_btn),
                        testTagId = "combo_five_btn_next",
                        modifier = Modifier.fillMaxWidth().height(64.dp).padding(horizontal = 8.dp)
                    ) {
                        if (page < quizList.value.lastIndex) {
                            scope.launch {
                                pagerState.animateScrollToPage(page + 1, animationSpec = tween(500))
                            }
                        } else {
                            navController.popBackStack()
                        }
                    }
                }
            }
        } else {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .align(Alignment.Center)
                    .padding(horizontal = 24.dp, vertical = 16.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                Text(
                    text = stringResource(Res.string.combo_title),
                    style = MaterialTheme.typography.displayLarge,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth().semantics { testTag = "combo_five_title" }
                )
                Text(
                    text = stringResource(Res.string.combo_subtitle_5_correct),
                    style = MaterialTheme.typography.headlineMedium,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth().semantics { testTag = "combo_five_subtitle" }
                )
                Text(
                    text = stringResource(Res.string.combo_coins_bonus),
                    style = MaterialTheme.typography.headlineSmall,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth().semantics { testTag = "combo_five_subtitle" }
                )
                Spacer(Modifier.size(24.dp))
                AlynFeedbackBox(
                    face = Faces.Happy,
                    text = stringResource(Res.string.combo_feedback),
                    userData = userData
                )
            }

            Column(
                modifier = Modifier.fillMaxWidth().align(Alignment.BottomCenter)
                    .padding(horizontal = 24.dp, vertical = 16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                GeroButton(
                    title = stringResource(Res.string.avanti_excl_btn),
                    testTagId = "combo_five_btn_next",
                    modifier = Modifier.fillMaxWidth().height(64.dp)
                ) {
                    if (page < quizList.value.lastIndex) {
                        scope.launch {
                            pagerState.animateScrollToPage(page + 1, animationSpec = tween(500))
                        }
                    } else {
                        navController.popBackStack()
                    }
                }
            }
        }

        if (showConfetti.value) {
            ConfettiKit(
                modifier = Modifier.fillMaxSize(),
                parties = listOf(
                    Party(
                        speed = 0f, maxSpeed = 30f, damping = 0.9f, spread = 360,
                        position = Position.Relative(0.0, 0.3),
                        emitter = Emitter(duration = 2L.toDuration(DurationUnit.SECONDS)).max(100)
                    )
                )
            )
            ConfettiKit(
                modifier = Modifier.fillMaxSize(),
                parties = listOf(
                    Party(
                        angle = 180,
                        speed = 0f, maxSpeed = 30f, damping = 0.9f, spread = 360,
                        position = Position.Relative(1.0, 0.3),
                        emitter = Emitter(duration = 5L.toDuration(DurationUnit.SECONDS)).max(100)
                    )
                )
            )
        }
    }
}

